import React, { useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

export type LibraryDrawerTitleChromeProps = {
  kindIcon: React.ReactNode;
  displayTitle: string;
  isEditingTitle: boolean;
  onRequestEditTitle: () => void;
  onTitleChange: (next: string) => void;
  onTitleBlur?: () => void;
  titleDisabled?: boolean;
  suggestedTitles?: string[];
  /** When true, show magic wand while editing (enabled when suggestedTitles exist) */
  showTitleSuggestions?: boolean;
  accessBar: React.ReactNode;
};

export function LibraryDrawerTitleChrome({
  kindIcon,
  displayTitle,
  isEditingTitle,
  onRequestEditTitle,
  onTitleChange,
  onTitleBlur,
  titleDisabled = false,
  suggestedTitles = [],
  showTitleSuggestions = false,
  accessBar,
}: LibraryDrawerTitleChromeProps) {
  const [wandAnchor, setWandAnchor] = useState<HTMLElement | null>(null);
  const wandTitles = suggestedTitles.filter((x) => Boolean(x?.trim()));
  const showWandSlot = isEditingTitle && showTitleSuggestions;
  const wandEnabled = wandTitles.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
        minWidth: 0,
        flex: 1,
        mr: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
        <Box sx={{ color: '#60a5fa', flexShrink: 0, display: 'flex', alignItems: 'center' }}>{kindIcon}</Box>

        {!isEditingTitle ? (
          <Typography
            variant="h6"
            sx={{
              color: '#fafafa',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {displayTitle || 'Untitled'}
          </Typography>
        ) : (
          <TextField
            value={displayTitle}
            onChange={(e) => onTitleChange(e.target.value)}
            onBlur={() => onTitleBlur?.()}
            variant="standard"
            disabled={titleDisabled}
            fullWidth
            placeholder="Title"
            InputProps={{
              sx: { color: '#fafafa', fontSize: '1.15rem', fontWeight: 600 },
              disableUnderline: false,
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiInput-underline:before': { borderBottomColor: '#3f3f46' },
              '& .MuiInput-underline:hover:before': { borderBottomColor: '#a1a1aa' },
              '& .MuiInput-underline:after': { borderBottomColor: '#60a5fa' },
            }}
          />
        )}

        {!titleDisabled && (
          <IconButton
            size="small"
            title={isEditingTitle ? 'Editing title' : 'Edit title'}
            onClick={() => {
              if (!isEditingTitle) onRequestEditTitle();
            }}
            disabled={isEditingTitle}
            sx={{ color: isEditingTitle ? '#52525b' : '#a1a1aa', '&:hover': { color: '#60a5fa' }, flexShrink: 0 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}

        {showWandSlot && (
          <>
            <IconButton
              size="small"
              title={wandEnabled ? 'Suggested titles' : 'No suggested titles for this draft'}
              disabled={!wandEnabled}
              onClick={(e) => wandEnabled && setWandAnchor(e.currentTarget)}
              sx={{
                flexShrink: 0,
                color: wandEnabled ? '#60a5fa' : '#3f3f46',
                '&.Mui-disabled': { color: '#3f3f46' },
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
            <Popover
              open={Boolean(wandAnchor) && wandEnabled}
              anchorEl={wandAnchor}
              onClose={() => setWandAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 1, minWidth: 280, maxWidth: 400, bgcolor: '#171717', border: '1px solid #27272a' }}>
                <Typography variant="caption" sx={{ color: '#a1a1aa', px: 1, display: 'block', mb: 0.5 }}>
                  Suggested titles
                </Typography>
                <List dense disablePadding>
                  {wandTitles.map((st, idx) => {
                    const selected = st.trim() === displayTitle.trim();
                    return (
                      <ListItemButton
                        key={idx}
                        onClick={() => {
                          onTitleChange(st);
                          setWandAnchor(null);
                        }}
                        sx={{
                          borderRadius: 1,
                          mb: 0.25,
                          border: '1px solid #27272a',
                          '&:hover': { bgcolor: '#27272a' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {selected ? (
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 22 }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ color: '#71717a', fontSize: 22 }} />
                          )}
                        </ListItemIcon>
                        <ListItemText primary={st} primaryTypographyProps={{ sx: { color: '#fafafa', fontSize: '0.9rem' } }} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            </Popover>
          </>
        )}
      </Box>

      <Box
        sx={{
          width: '100%',
          pt: 1.25,
          mt: 1,
          borderTop: '1px solid #27272a',
          minWidth: 0,
        }}
      >
        {accessBar}
      </Box>
    </Box>
  );
}
